// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {connect} from 'react-redux';
import {Actions, Layout, Model} from 'flexlayout-react';
import {
    camelCase,
    cloneDeep,
    forOwn,
    get,
    includes,
    indexOf,
    isArray, isEqual,
    isPlainObject,
    isString,
    kebabCase,
    set,
    upperCase,
} from 'lodash';

// Components
import Hotkeys from './hotkeys';
import Loading from './loading';
import MaximizePanelAction from '../app/maximize-panel-action';

// Redux Actions
import {layoutRegister, layoutUpdate} from '../../actions/layout-actions';

// Contexts
import {TabContext} from '../../contexts/tab-context';
import {PanelProvider} from '../../contexts/panel-context';

// Styles
import 'flexlayout-react/style/light.css';
import './flex-dashboard.scss';

class FlexDashboard extends Component {
    componentTypes = {};

    saveActions = [
        Actions.ADJUST_BORDER_SPLIT,
        Actions.MOVE_NODE,
        Actions.SET_ACTIVE_TABSET,
        Actions.SELECT_TAB,
        Actions.MAXIMIZE_TOGGLE,
    ];

    state = {
        defaultModel: this.getDefaultModel(),
        hotkeys: {},
        model: this.getLayoutModel(),
        panelActionButtons: {},
    };

    componentDidMount() {
        const {dashboardName, layoutRegister} = this.props;

        if (dashboardName) {
            layoutRegister(dashboardName);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { layoutData } = this.props;
        const { defaultModel, model: currentModel } = this.state;

        if (layoutData !== prevProps.layoutData) {
          const newConfig = layoutData || defaultModel;
          const newJson     = JSON.stringify(newConfig);
          const currentJson = JSON.stringify(currentModel.toJson());

          if (!isEqual(newJson, currentJson)) {
            const cloned = cloneDeep(newConfig);
            const fixed  = this.replaceConfig(cloned);
            const newModel = Model.fromJson(fixed);

            this.setState({ model: newModel });
          }
        }
    }

    getActiveTab() {
        const activeTabset = this.getActiveTabset();

        return activeTabset ? activeTabset.getSelectedNode() : null;
    }

    getActiveTabId() {
        const activeTab = this.getActiveTab();

        return activeTab ? activeTab.getId() : null;
    }

    getActiveTabset() {
        return this.layout ? this.layout.model?.getActiveTabset() : null;
    }

    getActiveTabsetId() {
        const activeTabset = this.getActiveTabset();

        return activeTabset ? activeTabset.getId() : null;
    }

    getMaximizedTabset() {
        return this.layout ? this.layout.model?.getMaximizedTabset() : null;
    }

    getMaximizedTabsetId() {
        const activeTabset = this.getMaximizedTabset();

        return activeTabset ? activeTabset.getId() : null;
    }

    getClass(subLayout) {
        const {className} = this.props;

        return subLayout ? 'flex-dashboard--layout' : classnames('flex-dashboard', className);
    }

    getComponentProps(node) {
        return get(node.getConfig(), 'componentProps');
    }

    getDefaultModel() {
        const {dashboardConfig, version = '1'} = this.props;
        const config = this.replaceConfig(cloneDeep(dashboardConfig));

        if (version) {
            set(config, 'layout.id', `version_${version}`);
        }

        return config;
    }

    getHotkeysProps() {
        const {hotkeys} = this.state;
        const currentHotkeys = hotkeys[this.getActiveTabId()] || {};

        return {
            hotkeys: {
                ...currentHotkeys,
            },
        };
    }

    getLayoutModel() {
        const {dashboardName, layoutData} = this.props;
        let model;

        if (dashboardName && layoutData) {
            if (this.isValidModel(layoutData)) {
                try {
                    model = Model.fromJson(this.replaceConfig(layoutData));
                } catch (e) {
                    model = null;
                }
            }
        }

        return model || Model.fromJson(this.getDefaultModel());
    }

    // ettslint-disable-next-line react/destructuring-assignment
    getLayoutProps(model) {
        const {model: modelState} = this.state;

        return {
            factory: this.factory,
            model: model || modelState,
            onAction: this.handleAction,
            // onRenderTab: this.handleRenderTab,
            onRenderTabSet: this.handleRenderTabSet,
            ref: (layout) => (this.layout = layout?.props),
        };
    }

    factory = (node) => {
        const component = node.getComponent();
        const ComponentNode = isString(component) ? this.componentTypes[component] : component;
        const panelProps = {
            node,
            onChange: this.handlePanelActionChange,
            onClick: this.handlePanelActionClick,
        };

        return ComponentNode ? (
            <TabContext.Provider
                value={{
                    deleteTab: this.deleteTab,
                    isTabActive: this.isTabActive,
                    isTabMaximized: this.isTabMaximized,
                    toggleTabMaximized: this.handleToggleFullscreen,
                    selectFirstTab: this.selectFirstTab,
                    selectTab: this.selectTab,
                }}
            >
                <PanelProvider {...panelProps}>
                    <MaximizePanelAction />
                    <ComponentNode dashboardProps={this.getComponentProps(node)} />
                </PanelProvider>
            </TabContext.Provider>
        ) : null;
    };

    findNode = (node, predicate) => {
        let result;
        const match = predicate(node);

        if (match) {
            result = node;
        } else {
            forOwn(node.getChildren(), (child, _tabIds) => {
                result = this.findNode(child, predicate);

                if (result) {
                    return false;
                }
            });
        }

        return result;
    };

    handleAction = (action) => {
        this.layoutModelDoAction(action);
        // save on *any* layout‐changing action
        if (!action.type.startsWith('FlexLayout_PerformDeprecated')) {
            this.saveLayout();
        }
        return action;
    };

    handlePanelActionChange = (panelId, buttons, hotkeys) => {
        this.setState((prevState) => ({
            hotkeys: {
                ...prevState.hotkeys,
                [panelId]: hotkeys,
            },
            panelActionButtons: {
                ...prevState.panelActionButtons,
                [panelId]: buttons,
            },
        }));
    };

    handlePanelActionClick = (node) => {
        this.selectTabset(node.getParent().getId());
    };

    handleRenderTabSet = (node, renderValues) => {
        // 1) stop dblclick on the built‑in header icons
        if (renderValues.headerButtons) {
            renderValues.headerButtons = renderValues.headerButtons.map((btn, idx) => (
                <span
                    key={`hdr-btn-${idx}`}
                    onDoubleClickCapture={e => e.stopPropagation()}
                    style={{ display: 'inline-block' }} // preserve layout
                >
                    {btn}
                </span>
            ));
        }
        // 2) stop dblclick on your custom panelActionButtons
        const selected = node.getSelectedNode();
        const { panelActionButtons } = this.state;
        if (selected) {
            const panelId = selected.getId();
            const panelButtons = get(panelActionButtons, panelId, []);
            if (panelButtons.length) {
                const wrapped = panelButtons.map((btn, idx) => (
                    <span
                        key={`custom-btn-${idx}`}
                        onDoubleClickCapture={e => e.stopPropagation()}
                        style={{ display: 'inline-block' }}
                    >
                        {btn}
                    </span>
                ));
                renderValues.buttons.push(...wrapped);
            }
        }
        // 3) MUST return the renderValues object
        return renderValues;
    };

    handleSelectTabNext = () => {
        const {tabIds} = this.layout;
        const tabIndex = indexOf(tabIds, this.getActiveTabId());
        const nextTabIndex = tabIndex < tabIds.length - 1 ? tabIndex + 1 : 0;
        const nextTab = tabIds[nextTabIndex];

        this.selectTab(nextTab);
    };

    handleSelectTabPrevious = () => {
        const {tabIds} = this.layout;
        const tabIndex = indexOf(tabIds, this.getActiveTabId());
        const nextTabIndex = tabIndex > 0 ? tabIndex - 1 : tabIds.length - 1;
        const nextTab = tabIds[nextTabIndex];

        this.selectTab(nextTab);
    };

    handleToggleFullscreen = (tabName) => {
        let nodeId;

        if (tabName) {
            nodeId = tabName;
        } else {
            const activeTabset = this.getActiveTabset();

            if (activeTabset) {
                nodeId = activeTabset.getId();
            }
        }

        if (nodeId) {
            this.layoutDoAction(Actions.maximizeToggle(nodeId));
        }
    };

    deleteTab = (tabName) => {
        this.layoutDoAction(Actions.deleteTab(tabName));
    };

    isTabActive = (tabName) => {
        const tabNode = this.layout && this.layout.model.getNodeById(tabName);

        return tabNode && tabNode.visible;
    };

    isTabMaximized = (tabName) => tabName && tabName === this.getMaximizedTabsetId();

    layoutDoAction = (action) => {
        const doAction = () => this.layout && this.layout.model.doAction(action);

        if (this.layout) {
            doAction();
        } else {
            setTimeout(doAction, 10);
        }
    };

    layoutModelDoAction = (action) => {
        if (this.layout && this.layout.model) {
            this.layout.model.doAction(action);
        }
    };

    saveLayout = () => {
        const {dashboardName, layoutUpdate} = this.props;
        // const {model} = this.state;
        const activeTabId = this.getActiveTabId();

        if (dashboardName && this.layout.model) {
            layoutUpdate(dashboardName, this.layout.model.toJson(), activeTabId);
        }
    };

    selectFirstTab = (tabIds) => {
        if (tabIds && tabIds.length) {
            const tab = this.findNode(this.layout.model.getRoot(), (node) => includes(tabIds, node.getId()));

            if (tab) {
                this.selectTab(tab.getId());
            }
        }
    };

    selectTab = (tabId) => {
        if (this.getActiveTabId() !== tabId) {
            this.layoutDoAction(Actions.selectTab(tabId));
        }
    };

    selectTabset = (tabsetId) => {
        if (this.getActiveTabsetId() !== tabsetId) {
            this.layoutDoAction(Actions.setActiveTabset(tabsetId));
        }
    };

    isValidModel(model) {
        const {version = '1'} = this.props;
        const validateConfig = (config) => {
            let valid = true;

            forOwn(config, (value, _key) => {
                if (isString(value?.component) && !this.componentTypes[value?.name]) {
                    valid = false;
                } else if (isPlainObject(value) || isArray(value)) {
                    valid = validateConfig(value);
                }
                return valid;
            });

            return valid;
        };
        let valid = validateConfig(model);

        if (valid && version) {
            const savedVersionId = get(model.layout, 'id', '').split('_');

            if (savedVersionId.length === 2 && savedVersionId[0] === 'version') {
                valid = savedVersionId[1] === version;
            } else {
                valid = false;
            }
        }

        return valid;
    }

    replaceConfig(config) {
        return forOwn(config, (value, _key) => {
            if (value) {
                if (value.name && (!value.id || /^#/.test(value.id))) {
                    value.id = upperCase(camelCase(value.name)).replace(/\s/g, '');
                }
                if (value.name && !isString(value.component)) {
                    this.componentTypes[value.name] = value.component;
                    value.component = value.name;
                    value.className = classnames('flex-dashboard--tab', `tab--${kebabCase(value.name)}`);
                } else if (isPlainObject(value) || isArray(value)) {
                    this.replaceConfig(value);
                }
            }
        });
    }

    renderLayout(model) {
        const {dashboardName} = this.props;
        const props = {
            className: this.getClass(!!model),
            id: `dashboard--${kebabCase(dashboardName)}`,
        };
        return (
            <TabContext.Provider
                value={{
                    deleteTab: this.deleteTab,
                    isTabActive: this.isTabActive,
                    isTabMaximized: this.isTabMaximized,
                    toggleTabMaximized: this.handleToggleFullscreen,
                    selectFirstTab: this.selectFirstTab,
                    selectTab: this.selectTab,
                }}
            >
                <div {...props}>
                    <Layout {...this.getLayoutProps(model)} />
                    <Hotkeys {...this.getHotkeysProps()} />
                </div>
            </TabContext.Provider>
        );
    }

    render() {
        const {preferencesFetched} = this.props;
        return preferencesFetched ? this.renderLayout() : <Loading loading={true} />;
    }
}

FlexDashboard.propTypes = {
    className: PropTypes.string,
    dashboardConfig: PropTypes.object,
    dashboardName: PropTypes.string.isRequired,
    isAdmin: PropTypes.func,
    layoutData: PropTypes.object,
    layoutRegister: PropTypes.func.isRequired,
    layoutUpdate: PropTypes.func.isRequired,
    preferencesFetched: PropTypes.bool,
    preferencesFetching: PropTypes.bool,
    version: PropTypes.string,
};

const mapStateToProps = (state, props) => ({
    layoutData: get(state.layout, ['layouts', props.dashboardName]),
    preferencesFetched: get(state.preferences, 'fetched'),
});

const mapDispatchToProps = {layoutRegister, layoutUpdate};

export default connect(mapStateToProps, mapDispatchToProps)(FlexDashboard);
